import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { Locale } from './locale'
// Sibling module, not the '@claritio/frontend' alias: vitest does not resolve
// that alias for test-file imports (see apps/frontend/README.md), and this
// module is pulled in transitively by respondentNav.ts / assessmentWizard.ts
// tests. A relative import needs no config in either runner.
import { BRAND } from './brand'
import { reloadForStaleChunk } from './lazyPage'

type Catalog = Record<string, object>

// One language's catalog is a chunk of its own, so a user downloads only the language they read.
const catalogs: Record<Locale, () => Promise<{ default: Catalog }>> = {
  en: () => import('../locales/en'),
  pl: () => import('../locales/pl'),
}

// Language is fixed at boot from the URL locale (see entry point).
export function initI18n(lng: Locale, resources: Partial<Record<Locale, Catalog>>) {
  i18n.use(initReactI18next).init({
    lng,
    fallbackLng: 'en',
    ns: ['common', 'admin', 'report', 'interview', 'blueprints', 'meetings'],
    defaultNS: 'common',
    resources,
    interpolation: {
      escapeValue: false, // React already escapes
      defaultVariables: { brand: BRAND.name }, // {{brand}} in any locale string, no per-call-site wiring
    },
    returnNull: false,
  })
  return i18n
}

// The page's language before the first render; English, the fallback, follows in the background.
export async function loadI18n(lng: Locale) {
  const { default: catalog } = await catalogs[lng]().catch(reloadForStaleChunk)
  initI18n(lng, { [lng]: catalog })
  if (lng === 'en') return
  catalogs.en().then(({ default: en }) => {
    for (const [ns, bundle] of Object.entries(en)) i18n.addResourceBundle('en', ns, bundle)
  }, () => {})
}

export default i18n
